import type { ReactElement, ReactNode } from 'react'
import { useId } from 'react'

import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  Flex,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TableScrollArea,
  TertiaryButton,
  Text,
  chakra,
} from '@enechain/ecloud-designsystem'

import { WEEKDAY_KEYS } from '~/components/common/DayGrid'
import type { CivilDate } from '~/models/civilDate'
import { civilDateEquals, formatCivilDateSlash } from '~/models/civilDate'
import { telopEmojiOf } from '~/models/weather/telop'
import type { WeeklySummaryColumn } from '~/pages/DemandForecastWeekly/models/weeklyPage'

type Props = {
  columns: WeeklySummaryColumn[]
  selected: CivilDate
  onSelect: (date: CivilDate) => void
  /** 指標の行を畳んでいるか。畳んでも日付の見出しは残し、選択日を切り替えられる (React mock と同じ)。 */
  isCollapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
}

type RowKey = Exclude<keyof WeeklySummaryColumn, 'date' | 'weatherCode'>

const ROWS: readonly RowKey[] = [
  'maxTemperature',
  'minTemperature',
  'normalDeviation',
  'maxPower',
  'minPower',
  'energy',
]

/** React mock `dayLabel` と同じ「M/D (曜)」。曜日は暦日から引くので timezone に依らない。 */
function dayLabelOf(date: CivilDate, t: TFunction): string {
  const weekday = new Date(
    Date.UTC(date.year, date.month - 1, date.day),
  ).getUTCDay()
  return t('common:date.withWeekday', {
    date: `${date.month}/${date.day}`,
    weekday: t(`common:date.calendar.weekday.${WEEKDAY_KEYS[weekday]}`),
  })
}

/**
 * 14 日を 1 つの表に並べたサマリ (AC-DFW-001)。日付の見出し・セルを押すと選択日が変わる (AC-DFW-002)。
 * 見た目は React mock `DemandForecastWeekly/components/WeeklySummaryTable` (見出し・左上の「日付」・選択日の背景・
 * 行の縞・天気アイコン)。縞は mock の `zebraRowBackground` と同じ偶数行の地を表の `data-zebra` で入れる。
 */
export const WeeklySummaryTable = ({
  columns,
  selected,
  onSelect,
  isCollapsed,
  onCollapsedChange,
}: Props): ReactElement => {
  const { t } = useTranslation()
  const titleId = useId()
  const cell = (
    col: WeeklySummaryColumn,
    content: ReactNode,
    testId: string,
  ): ReactElement => (
    <TableCell
      key={formatCivilDateSlash(col.date)}
      textAlign="right"
      whiteSpace="nowrap"
      cursor="pointer"
      aria-selected={civilDateEquals(col.date, selected)}
      data-testid={testId}
      onClick={() => onSelect(col.date)}
      paddingY={1}
    >
      {content}
    </TableCell>
  )
  const weatherIcon = (code: number | undefined): ReactNode => {
    if (code === undefined) return null
    // 表示名は表示言語に合わせる (jma-weather-data.md 付録「天気の表示名（日別 telopCode）」)。未知 code は '—'。
    const label = t(`common:weatherTelop.${code}`, { defaultValue: '—' })
    return (
      <chakra.span aria-label={label} role="img" title={label}>
        {telopEmojiOf(code, true)}
      </chakra.span>
    )
  }
  return (
    <Flex flexDirection="column" gap={2}>
      <Flex alignItems="center" flexWrap="wrap" gap={3}>
        <Text textStyle="sectionTitle" id={titleId}>
          {t('page:demandForecastWeekly.summary.label')}
        </Text>
        <TertiaryButton
          onClick={() => onCollapsedChange(!isCollapsed)}
          size="xs"
        >
          {t(
            isCollapsed
              ? 'page:demandForecastWeekly.summary.expand'
              : 'page:demandForecastWeekly.summary.collapse',
          )}
        </TertiaryButton>
      </Flex>
      <TableScrollArea>
        <TableRoot aria-labelledby={titleId} data-zebra size="sm">
          <TableHeader>
            <TableRow>
              <TableColumnHeader whiteSpace="nowrap" paddingY={1}>
                {t('page:demandForecastWeekly.summary.date')}
              </TableColumnHeader>
              {columns.map((col) => {
                const isSelected = civilDateEquals(col.date, selected)
                return (
                  <TableColumnHeader
                    key={formatCivilDateSlash(col.date)}
                    textAlign="right"
                    whiteSpace="nowrap"
                    aria-selected={isSelected}
                    backgroundColor={
                      isSelected ? 'Surface.accentPrimary' : undefined
                    }
                    paddingY={1}
                  >
                    <chakra.button
                      color="inherit"
                      cursor="pointer"
                      font="inherit"
                      onClick={() => onSelect(col.date)}
                      type="button"
                    >
                      {dayLabelOf(col.date, t)}
                    </chakra.button>
                  </TableColumnHeader>
                )
              })}
            </TableRow>
          </TableHeader>
          {!isCollapsed && (
            <TableBody>
              <TableRow>
                <TableColumnHeader whiteSpace="nowrap" paddingY={1} scope="row">
                  {t('page:demandForecastWeekly.summary.weather')}
                </TableColumnHeader>
                {columns.map((col) =>
                  cell(col, weatherIcon(col.weatherCode), 'weekly-weather'),
                )}
              </TableRow>
              {ROWS.map((row) => (
                <TableRow key={row}>
                  <TableColumnHeader
                    whiteSpace="nowrap"
                    paddingY={1}
                    scope="row"
                  >
                    {t(`page:demandForecastWeekly.summary.${row}`)}
                  </TableColumnHeader>
                  {columns.map((col) => cell(col, col[row], `weekly-${row}`))}
                </TableRow>
              ))}
            </TableBody>
          )}
        </TableRoot>
      </TableScrollArea>
    </Flex>
  )
}
